import type { FormikProps } from 'formik'
import type { CreateInvoicePayload } from './invoice.types'

/** The slice of Formik the toggle needs — kept narrow so it is trivial to test. */
export type InvoiceNumberToggleFormik = Pick<
  FormikProps<CreateInvoicePayload>,
  'values' | 'setFieldValue' | 'setValues' | 'setFieldTouched' | 'setFieldError'
>

/**
 * Handle the "Andere Rechnungsnummer" (custom invoice number) switch.
 *
 * Turning it off must flip the flag and clear the number in a single atomic `setValues`
 * pass. Doing it as two separate `setFieldValue` calls races: a validation pass can run
 * against `useCustomInvoiceNumber: true` with an already-empty `invoiceNumber`, which the
 * schema treats as the required field being blank — leaving a stuck "required" error that
 * blocks saving even though the option is no longer selected (SIB-860). We also reset the
 * field's touched/error state so nothing lingers from before the toggle.
 */
export function applyInvoiceNumberToggle(
  formik: InvoiceNumberToggleFormik,
  checked: boolean
): void {
  if (checked) {
    formik.setFieldValue('useCustomInvoiceNumber', true)
    return
  }

  formik.setValues(
    {
      ...formik.values,
      useCustomInvoiceNumber: false,
      invoiceNumber: '',
    },
    true
  )
  formik.setFieldTouched('invoiceNumber', false, false)
  formik.setFieldError('invoiceNumber', undefined)
}
